import { Action, ActionType, FrameStencil, MovementType } from "../../../types";
import {
  getBallActions,
  getBallMarkers,
  getPlayerAndPossessionActions,
  getPlayerMarkers,
  getPossessionActions,
} from "../../../utils";

export interface GetActionMaxTimeProps {
  action: Action;
  frameStencils: FrameStencil[];
  endTime: number;
}

export const getActionMaxTime = ({ action, frameStencils, endTime }: GetActionMaxTimeProps) => {
  const { actionType } = action;
  const playerAndPossessionActions = getPlayerAndPossessionActions(frameStencils);
  const possessionActions = getPossessionActions(frameStencils);
  const passes = getBallActions(frameStencils);

  const players = getPlayerMarkers(frameStencils);
  const balls = getBallMarkers(frameStencils);

  switch (actionType) {
    case ActionType.PLAYER: {
      const nextActions = playerAndPossessionActions.filter((a) => action.nextPlayerAction === a.id);

      // get pass that ends on player action
      const passOnPlayerActionEnd = passes.find((p) => p.playerActionEnd === action.id);
      const passOnPlayerActionEndTime = passOnPlayerActionEnd?.endTime || endTime;

      return Math.min(...nextActions.map(({ startTime }) => startTime), passOnPlayerActionEndTime, endTime);
    }
    case ActionType.BALL: {
      // find any passes following this pass
      // find the pass action on ball movement, then get the next pass startTime
      const nextPass = balls.flatMap((b) => {
        const passIndex = b.movements.findIndex((m) => m.id === action.id);
        const nextPass = b.movements.slice(passIndex + 1).find((m) => m.movementType === MovementType.BALL_ACTION);
        return passes.find((p) => p.id === nextPass?.id);
      });
      const nextPossessionMovementStartTime = nextPass[0]?.startTime || endTime;

      // find any possession actions following this pass's playerActionEnd
      const playerActionEnd = playerAndPossessionActions.find((a) => a.id === action.playerActionEnd);
      const nextPossessionAction = possessionActions.find((a) => a.id === playerActionEnd?.nextPlayerAction);
      const nextPossessionActionStartTime = nextPossessionAction?.startTime || endTime;

      return Math.min(nextPossessionMovementStartTime, nextPossessionActionStartTime, endTime);
    }
    case ActionType.POSSESSION: {
      const nextActions = playerAndPossessionActions.filter((a) => action.nextPlayerAction === a.id);

      // get passes that follow this possessionAction
      const playerOnPossessionAction = players.find((p) => p.movements.some((m) => m.id === action.id));
      const nextPass = balls.flatMap((b) => {
        const playerWithBallIndex = b.movements.findIndex((m) => m.id === playerOnPossessionAction?.id);
        const nextPass = b.movements
          .slice(playerWithBallIndex + 1)
          .find((m) => m.movementType === MovementType.BALL_ACTION);
        return passes.find((p) => p.id === nextPass?.id);
      });
      const nextPassStartTime = nextPass[0]?.startTime || endTime;

      return Math.min(...nextActions.map(({ startTime }) => startTime), nextPassStartTime, endTime);
    }

    case ActionType.HANDOFF:
      return endTime;
  }
};
